import { T } from '@/ui/Text/Text';
import React, { ReactNode, useCallback } from 'react';
import { Flex } from '@/ui/Flex/Flex';
import { Input } from '@/ui/Input/Input';
import {
  IconButton,
  IconBuzzer,
  IconHometimer,
  IconLED,
  IconNFCReader,
  IconScreen,
} from '@/icons';
import HorizontalDivider from '@/ui/HorizontalDivider';
import { useIntl } from 'react-intl';
import CustomSwitch from '@/ui/Switch/Switch';
import FAQSection from '../FAQSection';
import { StyledSettings, StyledSettingsContent } from '../styles';
import { GeneralSettingsType, HardwareKey, HardwareOverview } from '../types';

function GeneralSettings({
  settings,
  setSettings,
  disabled,
}: {
  settings: GeneralSettingsType;
  setSettings: React.Dispatch<React.SetStateAction<GeneralSettingsType>>;
  disabled?: boolean;
}) {
  const intl = useIntl();

  // Function to extract hardware overview from settings
  function extractHardwareOverview(): HardwareOverview[] {
    // Define the hardware keys to extract
    const hardwareKeys: HardwareKey[] = [
      'nfcReaders',
      'buttons',
      'buzzers',
      'leds',
      'screens',
    ];

    return hardwareKeys.map((key) => {
      // Filter out LEDs with identifier 'X' (which represents all LEDs for a specific channel)
      if (key === 'leds') {
        return {
          key,
          count: settings[key].filter((led) => led.ledIdentifier !== 'X')
            .length,
        };
      }
      return {
        key,
        count: settings[key].length,
      };
    });
  }
  const hardwareOverview = extractHardwareOverview();

  // Labels and icons for hardware overview
  const hardwareLabels: Record<
    HardwareKey,
    { label: string; icon: ReactNode }
  > = {
    nfcReaders: { label: 'NFC Readers', icon: <IconNFCReader /> },
    buttons: { label: 'Buttons', icon: <IconButton /> },
    buzzers: { label: 'Buzzers', icon: <IconBuzzer /> },
    leds: { label: 'LEDs', icon: <IconLED /> },
    screens: { label: 'Screens', icon: <IconScreen /> },
  };

  // Callback functions to handle settings updates
  const toggleHomeTimerDefinedinCMS = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      isHomeTimerDefinedInCMS: !prev.isHomeTimerDefinedInCMS,
    }));
  }, [setSettings]);

  const updateHomeTimer = useCallback(
    (e: number | string) => {
      setSettings((prev) => ({
        ...prev,
        homeTimer: Number(e) * 1000,
      }));
    },
    [setSettings],
  );

  const updateResetTimer = useCallback(
    (e: number | string) => {
      setSettings((prev) => ({
        ...prev,
        resetTimer: Number(e) * 1000,
      }));
    },
    [setSettings],
  );

  // TODO: add language register for the settings -> as multi select component

  const faqs = [
    {
      question: intl.formatMessage({
        defaultMessage: 'When are you able to use the Hometimer from the CMS?',
        id: 'Yy0P7T',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Currently you can only use the Hometimer from the CMS if you start the presentation mode from this app. This means only when you are using a Trihow Pad or other devices connected to your laptop. With Trihow Desks or other non-mobile devices the Hometimer needs to be defined in the code.',
        id: 'LiDoAN',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage:
          'What is the difference between Hometimer and Resettimer?',
        id: 'vN01TV',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'The Hometimer is the time the system needs to show the home slide again if no trihow is on a sensor. The Resettimer is the time the system needs to show the home slide regardless of whether a trihow lies on a sensor or not.',
        id: 'u9F3hD',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'What are the Hardware Settings?',
        id: 'MmVo00',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Here you can define which hardware is effectively used in your setup. Controllers are the devices that control the system, the Sensors are the devices that detect an event. Also there are is other Hardware like LEDs and Screen that is being used. Please refer to the person who set up the system for more details. Changes should only be made by the person who set up the system since they can break logic.',
        id: 'N7WG6c',
      }),
    },
  ];

  return (
    <StyledSettings data-testid="general-settings">
      <T h3>
        {intl.formatMessage({ defaultMessage: 'Settings', id: 'D3idYv' })}
      </T>
      <StyledSettingsContent>
        <Flex
          direction="column"
          gap={settings.isHomeTimerDefinedInCMS ? 8 : 16}
        >
          <Flex direction="column" gap={8}>
            <Flex justify="space-between" align="center">
              <T body1>
                {intl.formatMessage({
                  defaultMessage: 'Use Hometimer from CMS',
                  id: 'Iaia5L',
                })}
              </T>
              <CustomSwitch
                disabled={disabled ?? false}
                checked={settings.isHomeTimerDefinedInCMS}
                onChange={toggleHomeTimerDefinedinCMS}
              />
            </Flex>
            {!settings.isHomeTimerDefinedInCMS && (
              <Flex justify="space-between" align="center">
                <T body1>
                  {intl.formatMessage({
                    defaultMessage: 'Hometimer (in seconds)',
                    id: 'YscciC',
                  })}
                </T>
                <Input
                  disabled={disabled ?? false}
                  required
                  type="number"
                  size="small"
                  value={settings.homeTimer / 1000}
                  onChange={updateHomeTimer}
                  min={0}
                  icon={<IconHometimer />}
                />
              </Flex>
            )}
          </Flex>
          <Flex justify="space-between" align="center">
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Resettimer (in seconds)',
                id: 'RC9TPT',
              })}
            </T>
            <Input
              disabled={disabled ?? false}
              type="number"
              size="small"
              value={settings.resetTimer / 1000}
              onChange={updateResetTimer}
              min={0}
              icon={<IconHometimer />}
            />
          </Flex>
        </Flex>

        <HorizontalDivider />
        <Flex direction="column" gap={8}>
          <T body1 bold>
            {intl.formatMessage({
              defaultMessage: 'Registered Hardware',
              id: 'awmY5D',
            })}
          </T>
          <Flex direction="column" gap={4} align="flex-start">
            {!hardwareOverview.some((hardware) => hardware.count !== 0) && (
              <T body1>
                {intl.formatMessage({
                  defaultMessage: 'No hardware registered',
                  id: 'Ue2alV',
                })}
              </T>
            )}
            {hardwareOverview.map(({ key, count }) => {
              return count ? (
                <Flex align="center" gap={4}>
                  <T body1>{`${count}x`}</T>
                  {hardwareLabels[key].icon}
                  <T body1>{hardwareLabels[key].label}</T>
                </Flex>
              ) : null;
            })}
          </Flex>
        </Flex>
        <HorizontalDivider />
      </StyledSettingsContent>
      <FAQSection faqs={faqs} />
    </StyledSettings>
  );
}
export default GeneralSettings;
